import {css, styled} from '@mui/material/styles';
import clsx from 'clsx';
import {forwardRef} from 'react';
import {excludeComponentProps} from '../../utils/excludeComponentProps';
import {NavItem} from '../NavItem';
import {NavItemButton} from '../NavItemButton';
import {NavItemDropdown} from '../NavItemDropdown';
import {SharedNavProps} from './types';
import {getContainerMaxWidth, IsContained} from '../../helpers/getContainerMaxWidth';
import {navCssClassNames} from '../navCssClassNames';

export interface TopNavProps extends SharedNavProps {
    /**
     * Boolean to restrict wrap header and navigation content in a Container component. Use for contained layout.
     * @default false
     */
    isContained?: IsContained;
    /**
     * Control if dropdown-menu and mega-menu will have dividers.
     */
    hasDivider?: boolean;
}

const StyledNav = styled('nav')(({theme}) => css`
    display: block;
    padding: 0 ${theme.tokens.FuelSpacing2};
    background-color: ${theme.tokens.FuelColorWhite};
    border-bottom: solid 1px ${theme.tokens.FuelColorBorderLight};
    grid-area: top-nav;
`);

const HorizontalNavList = styled('ul', excludeComponentProps('isContained'))<Pick<TopNavProps, 'isContained'>>(({
    theme, isContained
}) => ({
    margin: '0 auto',
    padding: 0,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
    maxWidth: getContainerMaxWidth(theme, isContained),

    '.NavItem-root': {
        display: 'inline-block'
    }
}));

export const TopNav = forwardRef<HTMLElement, TopNavProps>(({
    children, items, isContained, hasDivider, className, ...props
}, ref) => (
    <StyledNav
        className={clsx(navCssClassNames.variantTop, 'TopNav-root', className)}
        ref={ref}
        {...props}
    >
        <HorizontalNavList
            className={navCssClassNames.list}
            isContained={isContained}
        >
            {children ?? items?.map(({
                label, subNavigationItems, ...itemProps
            }, itemIndex) => (
                <NavItem key={itemIndex}>
                    {subNavigationItems?.length ? (
                        <NavItemDropdown
                            {...itemProps}
                            hasDivider={hasDivider}
                            items={subNavigationItems}
                        >
                            {label}
                        </NavItemDropdown>
                    ) : (
                        <NavItemButton {...itemProps}>
                            {label}
                        </NavItemButton>
                    )}
                </NavItem>
            ))}
        </HorizontalNavList>
    </StyledNav>
));

TopNav.displayName = 'TopNav';
